// LEVELS, AREAS & SYSTEMS > 3D VOLUMES (owner 2026-10-29): the equal tiles over the real
// model, like Construction tracking, to tag 3D areas and sub-areas as the sign-off volumes
// the Sign-off timeline signs off, and to pull out the systems inside each one.
//
// Left: the volumes (areas, their sub-areas) and the one being edited. Middle: the model
// with its tiles - with "Tag tiles" on, a click adds or removes a tile, and a shift-click
// adds every shown tile in the box between it and the last one clicked; the layers slider
// and the level filter narrow what is shown, and a drawn area can fill a volume. Right:
// the systems in the volume (by the rule the area systems use), each one highlightable
// in the model and signable on its own.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { pickerOptions } from "../SignoffTimeline/projectPicker";
import { Alert, Button, Empty, Input, List, Popconfirm, Segmented, Select, Skeleton, Slider, Space, Switch, Tooltip } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";
import addNotification from "helpers/notify";
import { useRememberedProject } from "helpers/projectSelection";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import { createVolume, deleteVolume, getSignoffTiles, getSignoffTimeline, getVolumeElements, getVolumeSystems, getVolumes, updateVolume } from "api/signoff";
import TileModelView from "pages/SignoffTimeline/TileModelView";
import RequestSignoffModal from "pages/SignoffTimeline/RequestSignoffModal";
import { volumesByTile, innermost } from "pages/SignoffTimeline/signoffState";
import "pages/SignoffTimeline/signoff.scss";
import "./volumes.scss";

const GLASS = "#94a3b8";

/** Parents first, each followed by its sub-areas, with their depth. */
export function treeOf(volumes) {
  const kids = new Map();
  volumes.forEach((v) => { const p = v.parentId || ""; if (!kids.has(p)) kids.set(p, []); kids.get(p).push(v); });
  const ids = new Set(volumes.map((v) => v.id));
  const out = [];
  const walk = (p, depth) => (kids.get(p) || []).forEach((v) => { out.push({ ...v, depth }); if (depth < 10) walk(v.id, depth + 1); });
  walk("", 0);
  volumes.filter((v) => v.parentId && !ids.has(v.parentId)).forEach((v) => out.push({ ...v, depth: 0 }));
  return out;
}

/** The shown tiles in the box between two tiles (inclusive). */
export function boxBetween(list, a, b, visible) {
  const lo = (x, y) => Math.min(x, y);
  const hi = (x, y) => Math.max(x, y);
  return list.filter((t) => t.i >= lo(a.i, b.i) && t.i <= hi(a.i, b.i) && t.j >= lo(a.j, b.j) && t.j <= hi(a.j, b.j)
    && t.k >= lo(a.k, b.k) && t.k <= hi(a.k, b.k) && visible(t)).map((t) => t.key);
}

// EMBEDDED IN LEVELS, AREAS & SYSTEMS (owner 2026-11-01: 3D volumes merged into that page and
// taken off the Coordination tab strip): the page passes its own project, so no picker, no
// breadcrumbs and no route of its own.
export default function SignoffVolumesPage({ embedded = false, projectId: fixedProjectId = "" } = {}) {
  const { projectId: routeProjectId } = useParams();
  const history = useHistory();
  const userInfo = useSelector((st) => st.user.userInfo) || {};
  const [projects, setProjects] = useState([]);
  const [pickedId, setProjectId] = useRememberedProject(String(userInfo._id || userInfo.id || ""), projects, routeProjectId);
  const projectId = embedded ? fixedProjectId : pickedId || routeProjectId;

  const [tiles, setTiles] = useState(null);
  const [note, setNote] = useState("");
  const [tl, setTl] = useState(null);
  const [volumes, setVolumes] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [error, setError] = useState("");
  const [activeId, setActiveId] = useState("");
  const [tagging, setTagging] = useState(false);
  const [view, setView] = useState("3d");
  const [layers, setLayers] = useState(null); // [from, to]
  const [levelKey, setLevelKey] = useState("");
  const [onlyActive, setOnlyActive] = useState(false);
  const [systems, setSystems] = useState(null);
  const [system, setSystem] = useState("");
  const [uids, setUids] = useState(null);
  const [capturing, setCapturing] = useState(false); // the request window is rendering its views
  const [asking, setAsking] = useState(null);
  const [frameSeq, setFrameSeq] = useState(0);
  const lastClick = useRef(null);

  useEffect(() => {
    const t = localStorage.getItem(tokenRef);
    fetch(`${url}/projects/picker`, { headers: t ? { Authorization: `Bearer ${t}` } : {} })
      .then((r) => r.json())
      .then((d) => setProjects(Array.isArray(d) ? d : d?.projects || []))
      .catch(() => setProjects([]));
  }, []);
  useEffect(() => {
    if (!embedded && projectId && projectId !== routeProjectId) history.replace(`/areas/${projectId}?view=volumes`);
  }, [embedded, projectId, routeProjectId, history]);

  const reloadVolumes = useCallback(async () => {
    if (!projectId) return;
    const r = await getVolumes(projectId);
    setVolumes(r.volumes || []);
    setCanEdit(!!r.canEdit);
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    setTiles(null); setTl(null); setVolumes([]); setActiveId(""); setError(""); setLayers(null);
    Promise.all([getSignoffTiles(projectId), getSignoffTimeline(projectId), getVolumes(projectId)])
      .then(([t, line, v]) => {
        setTiles(t.tiles); setNote(t.note || ""); setTl(line); setVolumes(v.volumes || []); setCanEdit(!!v.canEdit);
        if (t.tiles) setLayers([0, t.tiles.grid.ny - 1]);
      })
      .catch((e) => setError(e.message));
  }, [projectId]);

  const list = useMemo(() => (tiles && tiles.tiles) || [], [tiles]);
  const volsOf = useMemo(() => volumesByTile(volumes), [volumes]);
  const byId = useMemo(() => new Map(volumes.map((v) => [v.id, v])), [volumes]);
  const active = byId.get(activeId) || null;
  const activeSet = useMemo(() => new Set((active && active.tiles) || []), [active]);
  const tree = useMemo(() => treeOf(volumes), [volumes]);

  const visible = useCallback((t) => {
    if (!t.n) return false;
    if (layers && (t.k < layers[0] || t.k > layers[1])) return false;
    if (levelKey && !(t.g || []).some((g) => g.l === levelKey)) return false;
    if (onlyActive && active && !activeSet.has(t.key)) return false;
    return true;
  }, [layers, levelKey, onlyActive, active, activeSet]);
  const styleOf = useCallback((t) => {
    if (active && activeSet.has(t.key)) return { color: active.color, strong: true };
    const v = innermost(volsOf.get(t.key), byId);
    return v ? { color: v.color, strong: true } : { color: GLASS, strong: false };
  }, [active, activeSet, volsOf, byId]);

  // the active volume's systems, and the elements tinted in the model. Picking another
  // volume starts afresh; a tile tagged or taken out reloads both quietly, once the clicks
  // pause - each click used to blank the list, drop the picked system and refetch every
  // element (2.9 MB on a BA03 volume), so the tint flickered off on every click.
  const tileCount = active ? active.tiles.length : 0;
  const [quietCount, setQuietCount] = useState(tileCount);
  useEffect(() => {
    const t = setTimeout(() => setQuietCount(tileCount), 700);
    return () => clearTimeout(t);
  }, [tileCount]);
  useEffect(() => {
    setSystems(null); setSystem(""); setUids(null);
  }, [activeId, projectId]);
  useEffect(() => {
    if (!activeId || !projectId) return undefined;
    let live = true;
    getVolumeSystems(projectId, activeId).then((r) => { if (live) setSystems(r); }).catch(() => { if (live) setSystems((was) => was || { systems: [], elements: 0, unclassified: 0 }); });
    return () => { live = false; };
  }, [activeId, projectId, quietCount]);
  const [uidsFor, setUidsFor] = useState(null); // the system the shown tint is for
  useEffect(() => {
    if (!activeId || !projectId) return undefined;
    let live = true;
    getVolumeElements(projectId, activeId, system).then((r) => { if (live) { setUids(r.uids); setUidsFor(system); } }).catch(() => {});
    return () => { live = false; };
  }, [activeId, projectId, system, quietCount]);
  const loadingSystem = uidsFor !== system;

  // SAVES GO ONE AT A TIME, IN CLICK ORDER, and the page shows each change at once: two
  // quick clicks used to both decide from the list as it was before either saved, so a
  // second click on a tile added it again instead of taking it out.
  const queue = useRef(Promise.resolve());
  const waiting = useRef(0);
  const showLocally = useCallback((id, body) => {
    setVolumes((vs) => vs.map((v) => {
      if (v.id !== id) return v;
      const next = { ...v };
      if (body.add || body.remove) {
        const keys = new Set(v.tiles || []);
        (body.add || []).forEach((k) => keys.add(k));
        (body.remove || []).forEach((k) => keys.delete(k));
        next.tiles = [...keys];
      }
      if (body.color) next.color = body.color;
      if (body.name) next.name = body.name;
      return next;
    }));
  }, []);
  const patch = useCallback((body, done) => {
    if (!active) return;
    const id = active.id;
    showLocally(id, body);
    waiting.current += 1;
    queue.current = queue.current.then(async () => {
      let failed = false;
      try {
        const r = await updateVolume(projectId, id, body);
        if (done) done(r);
      } catch (e) { failed = true; addNotification(e.message, "danger"); }
      waiting.current -= 1;
      // the server's copy once the clicks have all landed (or one failed), not in between,
      // where it would briefly take back the clicks still waiting
      if (!waiting.current || failed) await reloadVolumes().catch(() => {});
    });
  }, [active, projectId, reloadVolumes, showLocally]);
  // the colour picker reports every step of a drag; the colour is saved once it rests
  const colorTimer = useRef(null);
  const pickColor = (color) => {
    if (!active) return;
    showLocally(active.id, { color });
    clearTimeout(colorTimer.current);
    colorTimer.current = setTimeout(() => patch({ color }), 400);
  };
  useEffect(() => () => clearTimeout(colorTimer.current), []);

  const onPickTile = useCallback((t, e) => {
    if (!tagging || !active || !canEdit) return;
    if (e && e.shiftKey && lastClick.current) {
      const keys = boxBetween(list, lastClick.current, t, visible).filter((k) => !activeSet.has(k));
      lastClick.current = t;
      if (keys.length) patch({ add: keys }, () => addNotification(`${keys.length} tile${keys.length === 1 ? "" : "s"} added to ${active.name}`, "success"));
      return;
    }
    lastClick.current = t;
    patch(activeSet.has(t.key) ? { remove: [t.key] } : { add: [t.key] });
  }, [tagging, active, canEdit, list, visible, activeSet, patch]);

  const addVolume = async (parentId) => {
    try {
      const parent = parentId ? byId.get(parentId) : null;
      const n = volumes.filter((v) => (v.parentId || "") === (parentId || "")).length + 1;
      const r = await createVolume(projectId, { name: parent ? `${parent.name}.${n}` : `Area ${n}`, parentId: parentId || undefined });
      await reloadVolumes();
      setActiveId(r.volume.id);
      setTagging(true);
    } catch (e) { addNotification(e.message, "danger"); }
  };

  const shownKeys = () => list.filter(visible).map((t) => t.key);
  const levelOptions = useMemo(() => {
    const ks = new Set();
    list.forEach((t) => (t.g || []).forEach((g) => g.l && ks.add(g.l)));
    return [...ks].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).map((k) => ({ value: k, label: (tl && tl.levels[k]) || k }));
  }, [list, tl]);
  const areaOptions = useMemo(() => Object.entries((tl && tl.areas) || {}).filter(([, a]) => a.current)
    .map(([id, a]) => ({ value: id, label: [a.name, a.level].filter(Boolean).join(" · ") }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })), [tl]);
  const fillFromArea = (areaSetId) => {
    const keys = list.filter((t) => visible(t) && (t.g || []).some((g) => g.a === areaSetId || (g.aa || []).includes(areaSetId))).map((t) => t.key).filter((k) => !activeSet.has(k));
    if (!keys.length) { addNotification("No shown tile holds that area's elements", "warning"); return; }
    patch({ add: keys }, () => addNotification(`${keys.length} tiles added from the drawn area`, "success"));
  };

  const g = tiles && tiles.grid;
  const tilesNote = g ? `${g.edgeM} m tiles · ${g.nx} × ${g.nz} × ${g.ny}` : "";
  return (
    <div className={`signoff-page signoff-volumes ${embedded ? "is-embedded" : "container-fluid py-3"}`}>
      {embedded ? null : <CoordinationBreadcrumbs items={[{ label: "Levels, Areas & Systems", to: projectId ? `/areas/${projectId}` : "/areas" }]} current="3D volumes" />}
      <div className="signoff-page-head">
        <div>
          {embedded ? <h5 className="mb-0">3D volumes</h5> : <h2 className="mb-0">3D volumes</h2>}
          <div className="signoff-muted">Tag areas and sub-areas as tiles over the model. They become sign-off volumes on the Sign-off timeline, and show which systems sit inside them.</div>
        </div>
        <Space wrap>
          {embedded ? null : (
            <Select showSearch optionFilterProp="label" style={{ minWidth: 200 }} placeholder="Pick a project" value={projectId || undefined}
                    onChange={setProjectId} options={pickerOptions(projects, projectId, tl && tl.projectTitle)} />
          )}
          {tilesNote ? <span className="signoff-grid-chip">{tilesNote}</span> : null}
          {projectId ? <Button onClick={() => history.push(`/signoff-timeline/${projectId}`)}>Open Sign-off timeline</Button> : null}
        </Space>
      </div>
      {!projectId ? <p className="signoff-muted">Pick a project.</p> : null}
      {error ? <Alert type="error" showIcon message="The volumes could not be loaded" description={error} /> : null}
      {projectId && !error && !tl ? <Skeleton active paragraph={{ rows: 10 }} /> : null}
      {tl && !tiles ? <Alert type="info" showIcon message="No tiles yet" description={note} /> : null}
      {tl && tiles ? (
        <div className="volumes-grid">
          <div className="volumes-side">
            <div className="signoff-panel-title">Volumes</div>
            {canEdit ? (
              <Space className="mb-2" wrap>
                <Button size="small" icon={<PlusOutlined />} onClick={() => addVolume("")} data-testid="volume-add">Area</Button>
                <Button size="small" icon={<PlusOutlined />} disabled={!active} onClick={() => addVolume(activeId)}>Sub-area</Button>
              </Space>
            ) : null}
            {tree.length ? (
              <List size="small" dataSource={tree} renderItem={(v) => (
                <List.Item className={`volume-row ${v.id === activeId ? "is-active" : ""}`} onClick={() => { setActiveId(v.id === activeId ? "" : v.id); setFrameSeq((s) => s + 1); }}
                           style={{ paddingLeft: 8 + v.depth * 16 }}>
                  <span><i className="tile-swatch" style={{ background: v.color }} />{v.name}</span>
                  <small className="signoff-muted">{v.tiles.length.toLocaleString()} tiles</small>
                </List.Item>
              )} />
            ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={canEdit ? "Add an area, then click tiles in the model" : "No volumes yet"} />}
            {active && canEdit ? (
              <div className="volume-editor">
                <div className="tile-section">Edit</div>
                <Input key={`n-${active.id}`} defaultValue={active.name} onBlur={(e) => e.target.value.trim() && e.target.value !== active.name && patch({ name: e.target.value })} />
                <Space className="mt-2" wrap>
                  <input type="color" aria-label="Colour" value={active.color} onChange={(e) => pickColor(e.target.value)} />
                  <Select size="small" style={{ minWidth: 130 }} value={active.level || ""} onChange={(lv) => patch({ level: lv })}
                          options={[{ value: "", label: "No level" }, ...levelOptions.map((o) => ({ value: o.label, label: o.label }))]} />
                  <Popconfirm title={`Remove ${active.name}${volumes.some((v) => v.parentId === active.id) ? " and its sub-areas" : ""}?`}
                              onConfirm={async () => { try { await deleteVolume(projectId, active.id); setActiveId(""); await reloadVolumes(); } catch (e) { addNotification(e.message, "danger"); } }}>
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
                {active.parentId && byId.get(active.parentId) ? <small className="signoff-muted d-block mt-1">Sub-area of {byId.get(active.parentId).name}: its tiles are always in the area too.</small> : null}
              </div>
            ) : null}
          </div>

          <div className="volumes-center">
            <div className="signoff-scene-bar">
              <Segmented value={view} onChange={setView} options={[{ value: "3d", label: "3D" }, { value: "plan", label: "Plan" }, { value: "section", label: "Section" }]} />
              {canEdit ? (
                <Tooltip title={active ? "Click a tile to add or remove it; shift-click adds the box between two tiles" : "Pick or add a volume first"}>
                  <span><Switch checked={tagging && !!active} disabled={!active} onChange={setTagging} /> Tag tiles</span>
                </Tooltip>
              ) : null}
              <Select value={levelKey} onChange={setLevelKey} style={{ minWidth: 120 }} options={[{ value: "", label: "All levels" }, ...levelOptions]} />
              {active ? <span><Switch size="small" checked={onlyActive} onChange={setOnlyActive} /> Only {active.name}</span> : null}
              {active && canEdit ? (
                <>
                  <Select showSearch optionFilterProp="label" value={null} placeholder="Fill from a drawn area" style={{ minWidth: 180 }} options={areaOptions} onChange={fillFromArea} />
                  <Button size="small" onClick={() => { const keys = shownKeys().filter((k) => !activeSet.has(k)); if (keys.length) patch({ add: keys }); }}>Add shown tiles</Button>
                  <Button size="small" onClick={() => { const keys = shownKeys().filter((k) => activeSet.has(k)); if (keys.length) patch({ remove: keys }); }}>Remove shown tiles</Button>
                </>
              ) : null}
            </div>
            {layers ? (
              <div className="volumes-layers">
                <span className="signoff-muted">Layers</span>
                <Slider range min={0} max={Math.max(0, g.ny - 1)} value={layers} onChange={setLayers}
                        tooltip={{ formatter: (k) => `${Math.round((g.origin.y + k * g.edge) * g.unitM * 10) / 10} m` }} />
              </div>
            ) : null}
            {/* steps aside while the request window renders its views (one viewer at a time) */}
            {!capturing ? (
              <TileModelView projectId={projectId} tiles={tiles} styleOf={styleOf} visible={visible} outline={active ? activeSet : null} outlineColor={0x111827}
                             onPickTile={onPickTile} highlightUids={uids} view={view} frameKeys={active ? active.tiles : null} frameSeq={frameSeq} />
            ) : <div className="signoff-muted p-4">The model is rendering the sign-off views in the request window…</div>}
          </div>

          <div className="volumes-side">
            <div className="signoff-panel-title">Systems{active ? ` in ${active.name}` : ""}</div>
            {!active ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Pick a volume" /> : null}
            {active && !systems ? <Skeleton active paragraph={{ rows: 4 }} /> : null}
            {active && systems ? (
              <>
                <div className={`tile-row volume-system ${!system ? "is-active" : ""}`} onClick={() => setSystem("")}><span>All elements</span><b>{systems.elements.toLocaleString()}</b></div>
                {systems.systems.map((s) => (
                  <div key={s.system} className={`tile-row volume-system ${system === s.system ? "is-active" : ""}`} onClick={() => setSystem(system === s.system ? "" : s.system)}>
                    <span>{s.system}{system === s.system && loadingSystem ? <small className="signoff-muted"> · finding elements…</small> : null}</span><span>{s.n.toLocaleString()}</span>
                  </div>
                ))}
                {systems.unclassified ? <div className="tile-row signoff-muted"><span>No system</span><span>{systems.unclassified.toLocaleString()}</span></div> : null}
                <small className="signoff-muted d-block mt-1">Click a system to highlight its elements in the model.</small>
                {tl.canRequest && active.tiles.length ? (
                  <Button className="mt-3" type="primary" block
                          onClick={() => setAsking([{ kind: "volume", volumeId: active.id, ...(system ? { system } : {}) }])}>
                    Request sign-off · {system ? `${system} in ` : ""}{active.name}
                  </Button>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      ) : null}
      {asking ? (
        <RequestSignoffModal projectId={projectId} areas={tl.areas} levels={tl.levels} tiles={list} tileView={tiles} volumes={volumes} initial={asking}
                             onClose={() => setAsking(null)} onSent={() => setAsking(null)} onCapturing={setCapturing} />
      ) : null}
    </div>
  );
}
